import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableHeaderFilter } from "@lib/types/Api";
import { useEffect, useMemo, useState } from "react";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { Invoice } from "@lib/types/Billing";
import { InvoiceDrawer } from "@lib/components/PageComponents/Invoice";
import { useCashPaymentQueries } from "@lib/apiQueries";
import { SharedContextProps } from "@lib/types";

const SharedCashPaymentHistory: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  customerId,
  pageIsActive = true,
  withoutContainer = false,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  // const { state } = useLocation();

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["invoice"],
    filter: initialFilters,
  });

  const queryKey = ["cash-payment-history"];
  const { cashPaymentsQuery, deleteCashPaymentMutation } =
    useCashPaymentQueries({
      params,
      messages,
      queryKey,
      enabled: pageIsActive,
    });

  const { data, isLoading, isFetching } = cashPaymentsQuery;
  const apiData = data?.data?.data;

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selected, setSelected] = useState<Invoice>();
  useEffect(() => {
    if (!openDrawer) setSelected(null);
  }, [openDrawer]);

  const toggleDrawer = (status?: boolean) => {
    if (typeof status !== "undefined") setOpenDrawer(status);
    else setOpenDrawer(!openDrawer);
  };

  const handleSelectInvoice = (invoice: Invoice) => {
    setSelected(invoice);
    toggleDrawer(true);
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        apiData,
        messages,
        deleteQuery: deleteCashPaymentMutation,
        navigate,
        setParams,
        handleSelectInvoice,
      }),
    [apiData, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !!f.title) as TableHeaderFilter[];
  }, [params]);

  const title =
    customTitle || (messages["common.cashPaymentHistory"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={cashPaymentsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          minusHeight={minusHeight}
        />
      </AppsContent>

      <InvoiceDrawer
        invoice={selected}
        openDrawer={openDrawer}
        invoiceListQueryKey={queryKey}
        setSelectedInvoice={handleSelectInvoice}
        toggleDrawer={toggleDrawer}
        tabToBeOpenedOnFooter="2"
      />
    </AppsContainer>
  );
};

export default SharedCashPaymentHistory;
